import { Badge, Button, Surface } from '@chopchop-se/ui';
import type { ReceivingScanResult } from '@warehouse/shared';
import { AlertTriangle, Barcode, Boxes, MapPin, PackageSearch } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { formatQty } from '../../../lib/format';
import { trimQty } from '../format';

/**
 * What the last scan turned out to be — the operator's feedback surface.
 *
 * Everything the server understood is shown, including the label's own
 * warnings: a truncated GTIN or a failed check digit is a real event at the
 * dock, and the old system's habit of silently dropping it is exactly how a
 * wrong article gets received.
 */
export function ScanResultCard({
  scan,
  onTeach,
  canTeach,
}: {
  scan: ReceivingScanResult;
  onTeach?: (code: string) => void;
  canTeach?: boolean;
}): ReactNode {
  const { t } = useTranslation('receiving');

  return (
    <Surface className="p-4" data-testid="scan-result">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 space-y-2">
          {scan.article ? (
            <div className="flex flex-wrap items-center gap-2">
              <Boxes className="size-4 text-brand-600" aria-hidden />
              <span className="font-medium text-ink">{scan.article.name}</span>
              <span className="font-mono text-xs text-ink-muted">{scan.article.articleNo}</span>
              {scan.article.supplierScoped ? (
                <Badge tone="info" size="sm">
                  {t('scan.supplierScoped')}
                </Badge>
              ) : null}
            </div>
          ) : null}

          <dl className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
            {scan.gtin14 ? <Fact label="GTIN" value={scan.gtin14} mono /> : null}
            {scan.lotNo ? <Fact label={t('scan.lot')} value={scan.lotNo} mono /> : null}
            {scan.bestBefore ? <Fact label={t('scan.bestBefore')} value={scan.bestBefore} /> : null}
            {scan.sscc ? <Fact label={t('scan.sscc')} value={scan.sscc} mono /> : null}
            {scan.quantity ? (
              <Fact
                label={t('scan.quantity')}
                value={`${formatQty(scan.quantity)}${scan.quantityUnit === 'kg' ? ' kg' : ''}`}
              />
            ) : null}
            {scan.article && Number(scan.article.qtyPerScan) !== 1 ? (
              <Fact
                label={t('scan.result')}
                value={t('scan.qtyPerScan', {
                  qty: trimQty(scan.article.qtyPerScan),
                  unit: scan.article.unitCode ?? scan.article.baseUnitCode,
                })}
              />
            ) : null}
          </dl>

          {scan.pallet ? (
            <p className="flex items-center gap-2 text-sm text-ink">
              <PackageSearch className="size-4 text-ink-muted" aria-hidden />
              <span className="font-mono">{t('scan.palletFound', { lpn: scan.pallet.lpn })}</span>
              {scan.pallet.currentBinCode ? (
                <span className="text-ink-muted">
                  {t('scan.palletBin', { bin: scan.pallet.currentBinCode })}
                </span>
              ) : (
                <span className="text-ink-muted">{t('scan.palletEmpty')}</span>
              )}
            </p>
          ) : null}

          {scan.ssccUnknown ? (
            <p className="text-sm text-ink-muted">{t('scan.ssccNew')}</p>
          ) : null}

          {scan.bin ? (
            <p className="flex items-center gap-2 text-sm text-ink">
              <MapPin className="size-4 text-ink-muted" aria-hidden />
              <span className="font-mono">{t('scan.binFound', { code: scan.bin.binCode })}</span>
              {scan.bin.isBlocked ? (
                <Badge tone="danger" size="sm">
                  {t('scan.binBlocked')}
                </Badge>
              ) : null}
            </p>
          ) : null}

          {scan.ambiguousArticleIds.length > 0 ? (
            <p className="text-sm text-danger-600">{t('scan.ambiguous')}</p>
          ) : null}

          {scan.needsTeach ? (
            <div className="space-y-1">
              <p className="flex items-center gap-2 text-sm font-medium text-ink">
                <Barcode className="size-4 text-warning-600" aria-hidden />
                {t('scan.unknown')}
                <span className="font-mono text-xs text-ink-muted">{scan.data}</span>
              </p>
              <p className="text-xs text-ink-muted">{t('scan.unknownHint')}</p>
            </div>
          ) : null}

          {scan.warnings.length > 0 ? (
            <details className="text-xs text-ink-muted">
              <summary className="flex cursor-pointer items-center gap-1.5 text-warning-700">
                <AlertTriangle className="size-3.5" aria-hidden />
                {t('scan.warnings')} ({scan.warnings.length})
              </summary>
              <ul className="mt-1 list-disc space-y-0.5 pl-5">
                {scan.warnings.map((warning) => (
                  <li key={`${warning.code}-${warning.message}`}>
                    <span className="font-mono">{warning.code}</span> — {warning.message}
                  </li>
                ))}
              </ul>
            </details>
          ) : null}
        </div>

        {scan.needsTeach && canTeach && onTeach ? (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              onTeach(scan.data);
            }}
          >
            {t('scan.teach')}
          </Button>
        ) : null}
      </div>
    </Surface>
  );
}

function Fact({ label, value, mono }: { label: string; value: string; mono?: boolean }): ReactNode {
  return (
    <div className="flex items-baseline gap-1.5">
      <dt className="text-xs uppercase tracking-wide text-ink-muted">{label}</dt>
      <dd className={mono ? 'font-mono text-ink' : 'text-ink'}>{value}</dd>
    </div>
  );
}
